/**
 * Backend API Service
 *
 * This service handles API requests authenticated with the backend JWT token.
 * It specifically does NOT use Firebase authentication.
 */
import { backendApiUrl } from '../config/backendApiUrl';
import { useBackendAuth } from '../contexts/BackendAuthContext';

const resolveBackendServiceUrl = (url) => {
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/api/') || url === '/api'
    ? url
    : `/api${url.startsWith('/') ? url : `/${url}`}`;
  return backendApiUrl(path);
};

/**
 * Make an authenticated API request with the backend JWT token
 * (This function remains for authenticated calls)
 */
export const fetchWithBackendAuth = async (
  url,
  method = 'GET',
  data = null,
  customHeaders = {},
  token
) => {
  try {
    if (!token) {
      throw new Error('BACKEND_AUTH_ERROR: No backend authentication token provided');
    }

    const fullUrl = resolveBackendServiceUrl(url);
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...customHeaders,
    };
    const options = {
      method,
      headers,
      credentials: 'include',
    };

    if (data && method !== 'GET') {
      options.body = JSON.stringify(data);
    }

    console.log(`[Backend API Auth] ${method} ${fullUrl}`);
    const response = await fetch(fullUrl, options);

    if (response.status === 401) {
      throw new Error('BACKEND_AUTH_ERROR: Authentication token expired or invalid');
    }
    if (response.status === 403) {
      throw new Error('BACKEND_AUTH_ERROR: Insufficient permissions for this operation');
    }

    let responseData;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (!response.ok) {
      const error = new Error(responseData.message || responseData || 'API request failed');
      error.status = response.status;
      error.response = responseData;
      throw error;
    }
    return responseData;
  } catch (error) {
    console.error('Backend API authenticated request failed:', error);
    if (error.message && error.message.startsWith('BACKEND_AUTH_ERROR:')) {
      error.isAuthError = true;
      error.authErrorType = error.message.split(':')[1].trim();
    }
    throw error;
  }
};

/**
 * Backend API Service Hook
 */
export const useBackendApiService = () => {
  const { backendToken, logout } = useBackendAuth();

  const request = async (url, method, data = null, customHeaders = {}) => {
    try {
      return await fetchWithBackendAuth(url, method, data, customHeaders, backendToken);
    } catch (error) {
      if (
        error.status === 401 ||
        (error.isAuthError && error.authErrorType === 'Authentication token expired or invalid')
      ) {
        console.error('Backend token expired or invalid, logging out');
        logout();
      }
      throw error;
    }
  };

  const get = (url, customHeaders = {}) => request(url, 'GET', null, customHeaders);
  const post = (url, data, customHeaders = {}) => request(url, 'POST', data, customHeaders);
  const put = (url, data, customHeaders = {}) => request(url, 'PUT', data, customHeaders);
  const del = (url, customHeaders = {}) => request(url, 'DELETE', null, customHeaders);

  /**
   * NEW: POST request without backend auth token
   */
  const postUnauthenticated = async (url, data, customHeaders = {}) => {
    try {
      const fullUrl = resolveBackendServiceUrl(url);
      const headers = {
        'Content-Type': 'application/json',
        ...customHeaders,
      };
      const options = {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      };
      console.log(`[Backend API Unauthenticated] POST ${fullUrl}`);
      const response = await fetch(fullUrl, options);

      let responseData;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        // Create a more descriptive error message
        const errorMessage =
          typeof responseData === 'object'
            ? JSON.stringify(responseData)
            : responseData || 'API request failed';

        const error = new Error(errorMessage);
        error.status = response.status;
        error.response = responseData;
        throw error;
      }
      return responseData;
    } catch (error) {
      console.error('Backend API unauthenticated request failed:', error);
      throw error;
    }
  };

  return {
    request,
    get,
    post,
    put,
    delete: del,
    postUnauthenticated, // Expose the new unauthenticated POST method
    isAuthenticated: !!backendToken,
  };
};

export default useBackendApiService;
